import { Container, Graphics, Text, type Texture } from 'pixi.js';
import { DEFAULT_CELL, cellsToSpec, coerceCell, type ScriptCell } from '../dev/battleScript';
import type { DesignViewport } from '../render/layout';
import { ActionPalette } from './ActionPalette';
import { makeImageButton } from './Button';
import { OrnateBorder } from './OrnateBorder';
import { ScriptCellView, type ScriptIcons } from './ScriptCell';

const PANEL_MAX_W = 760;
const PANEL_MAX_H = 350;
const PANEL_W_FRAC = 0.94;
const PANEL_H_FRAC = 0.94;
const PANEL_COLOR = 0x2a2018;
const PANEL_BORDER = 0xb38a4a;
const LABEL_COLOR = 0xd8c39a;
const PAD = 16;
const ROW_LABEL_W = 26;
const CELL_GAP = 6;
const MIN_ROUNDS = 1;
const MAX_ROUNDS = 10;
const DEFAULT_ROUNDS = 3;
// Auto-saved script survives code reloads so testers don't refill the grid.
const STORAGE_KEY = 'versus.debugBattleScript';

export type BattleSetupModalOptions = {
  icons: ScriptIcons;
  selectBtn: Texture;
  border: Texture;
  corner: Texture;
  viewport: () => DesignViewport;
  fontFamily?: string;
  textResolution: number;
  onFight: (p1Spec: string, p2Spec: string) => void;
  onRandom: () => void;
  onClose: () => void;
};

// Pre-battle setup overlay: pick RANDOM or compose a scripted battle in a
// per-round, per-fighter icon grid. Lives as a stage sibling above the desktop;
// main.ts toggles visibility. State persists across opens.
export class BattleSetupModal extends Container {
  private readonly opts: BattleSetupModalOptions;
  private readonly scrim = new Graphics();
  private readonly panel = new Container();
  private readonly panelBg = new Graphics();
  private readonly body = new Container();
  private frame: OrnateBorder | null = null;
  private palette: ActionPalette | null = null;
  private errorText = '';

  private p1: ScriptCell[];
  private p2: ScriptCell[];

  private panelW = 0;
  private panelH = 0;

  constructor(opts: BattleSetupModalOptions) {
    super();
    this.opts = opts;

    const saved = loadPersisted();
    this.p1 = saved?.p1 ?? makeRow(DEFAULT_ROUNDS);
    this.p2 = saved?.p2 ?? makeRow(DEFAULT_ROUNDS);

    this.scrim.eventMode = 'static'; // block input to the desktop behind
    this.addChild(this.scrim);

    this.panel.addChild(this.panelBg);
    this.panel.addChild(this.body);
    this.addChild(this.panel);

    window.addEventListener('resize', this.onResize);
    this.layout();
  }

  private readonly onResize = (): void => {
    if (this.visible) this.layout();
  };

  override destroy(...args: Parameters<Container['destroy']>): void {
    window.removeEventListener('resize', this.onResize);
    super.destroy(...args);
  }

  show(): void {
    this.visible = true;
    this.errorText = '';
    this.closePalette();
    this.layout();
  }

  showError(message: string): void {
    this.errorText = message;
    this.rebuild();
  }

  private get rounds(): number {
    return this.p1.length;
  }

  private setRounds(n: number): void {
    const next = Math.max(MIN_ROUNDS, Math.min(MAX_ROUNDS, n));
    while (this.p1.length < next) {
      this.p1.push({ ...DEFAULT_CELL });
      this.p2.push({ ...DEFAULT_CELL });
    }
    this.p1.length = next;
    this.p2.length = next;
    this.errorText = '';
    this.persist();
    this.rebuild();
  }

  // Wipes the script back to the default empty grid.
  private reset(): void {
    this.p1 = makeRow(DEFAULT_ROUNDS);
    this.p2 = makeRow(DEFAULT_ROUNDS);
    this.errorText = '';
    this.closePalette();
    this.persist();
    this.rebuild();
  }

  private persist(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ p1: this.p1, p2: this.p2 }));
    } catch {
      // storage unavailable (private mode / quota) - dev convenience, non-fatal
    }
  }

  // Sizes the scrim to the whole viewport and centers the panel; then redraws.
  private layout(): void {
    const vp = this.opts.viewport();
    this.scrim.clear().rect(vp.x, vp.y, vp.width, vp.height).fill({ color: 0x000000, alpha: 0.62 });

    this.panelW = Math.min(PANEL_MAX_W, vp.width * PANEL_W_FRAC);
    this.panelH = Math.min(PANEL_MAX_H, vp.height * PANEL_H_FRAC);
    this.panel.position.set(
      vp.x + (vp.width - this.panelW) / 2,
      vp.y + (vp.height - this.panelH) / 2,
    );
    // Plain rect fill - the brass OrnateBorder (rectangular) frames the edges.
    this.panelBg.clear().rect(0, 0, this.panelW, this.panelH).fill(PANEL_COLOR);

    // Brass frame on top of the fill, below the content. Rebuilt with the panel
    // since its geometry depends on panelW/panelH.
    if (this.frame) this.frame.destroy({ children: true });
    this.frame = new OrnateBorder({
      width: this.panelW,
      height: this.panelH,
      border: this.opts.border,
      corner: this.opts.corner,
    });
    this.panel.addChildAt(this.frame, 1); // above panelBg(0), below body

    this.rebuild();
  }

  private rebuild(): void {
    this.body.removeChildren().forEach((c) => c.destroy());
    const innerW = this.panelW - PAD * 2;
    const cx = this.panelW / 2;

    this.addText('Debug Mode', cx, 12, { size: 16, bold: true, color: LABEL_COLOR, anchorX: 0.5 });

    const cell = this.cellSize(innerW);
    const rowsTop = 40;
    const p1y = rowsTop;
    const p2y = rowsTop + cell + 22;
    this.addFighterRow('P1', this.p1, p1y, cell);
    this.addFighterRow('P2', this.p2, p2y, cell);
    this.addRoundTicks(p2y + cell + 2, cell);

    // Rounds +/- control.
    const ctrlY = p2y + cell + 20;
    this.addText(`ROUNDS: ${this.rounds}`, PAD, ctrlY, { size: 12, color: LABEL_COLOR });
    this.addSmallButton('-', PAD + 88, ctrlY - 2, () => this.setRounds(this.rounds - 1));
    this.addSmallButton('+', PAD + 116, ctrlY - 2, () => this.setRounds(this.rounds + 1));

    // DSL preview (secondary aid).
    const previewY = ctrlY + 26;
    this.addText(`P1: ${cellsToSpec(this.p1)}`, PAD, previewY, { size: 10, color: 0x9b8a6a });
    this.addText(`P2: ${cellsToSpec(this.p2)}`, PAD, previewY + 14, { size: 10, color: 0x9b8a6a });

    if (this.errorText) {
      this.addText(this.errorText, cx, previewY + 30, { size: 11, color: 0xff6655, anchorX: 0.5 });
    }

    // Bottom buttons (centered on their points; ~114px wide at height 34).
    const by = this.panelH - 36;
    this.addMainButton('RANDOM', cx - 130, by, () => this.opts.onRandom());
    this.addMainButton('RESET', cx, by, () => this.reset());
    this.addMainButton('FIGHT', cx + 130, by, () => this.startFight());
    this.addSmallButton('X', this.panelW - 45, 22, () => this.opts.onClose());
  }

  private startFight(): void {
    this.opts.onFight(cellsToSpec(this.p1), cellsToSpec(this.p2));
  }

  private cellSize(innerW: number): number {
    const avail = innerW - ROW_LABEL_W - CELL_GAP;
    const raw = (avail - (this.rounds - 1) * CELL_GAP) / this.rounds;
    return Math.max(28, Math.min(60, raw));
  }

  private rowStartX(): number {
    return PAD + ROW_LABEL_W + CELL_GAP;
  }

  private addFighterRow(label: string, cells: ScriptCell[], y: number, cell: number): void {
    this.addText(label, PAD, y + cell / 2 - 7, { size: 13, bold: true, color: LABEL_COLOR });
    let x = this.rowStartX();
    cells.forEach((c, i) => {
      const view = new ScriptCellView({
        cell: c,
        icons: this.opts.icons,
        size: cell,
        fontFamily: this.opts.fontFamily,
        textResolution: this.opts.textResolution,
        onTap: () => this.openPalette(cells, i),
      });
      view.position.set(x, y);
      this.body.addChild(view);
      x += cell + CELL_GAP;
    });
  }

  private addRoundTicks(y: number, cell: number): void {
    let x = this.rowStartX();
    for (let i = 0; i < this.rounds; i++) {
      this.addText(`${i + 1}`, x + cell / 2, y, { size: 9, color: 0x9b8a6a, anchorX: 0.5 });
      x += cell + CELL_GAP;
    }
  }

  private openPalette(cells: ScriptCell[], index: number): void {
    this.closePalette();
    const current = cells[index];
    if (!current) return;
    this.palette = new ActionPalette({
      cell: current,
      icons: this.opts.icons,
      selectBtn: this.opts.selectBtn,
      width: this.panelW,
      height: this.panelH,
      fontFamily: this.opts.fontFamily,
      textResolution: this.opts.textResolution,
      onChange: (updated) => {
        cells[index] = updated;
        this.errorText = '';
        this.persist();
        this.rebuild();
        this.panel.setChildIndex(this.palette!, this.panel.children.length - 1);
      },
      onClose: () => this.closePalette(),
    });
    this.panel.addChild(this.palette);
  }

  private closePalette(): void {
    if (this.palette) {
      this.palette.destroy({ children: true });
      this.palette = null;
    }
  }

  // ----- small render helpers -----

  private addText(
    text: string,
    x: number,
    y: number,
    o: { size: number; bold?: boolean; color: number; anchorX?: number },
  ): void {
    const t = new Text({
      text,
      style: {
        fontFamily: this.opts.fontFamily,
        fontSize: o.size,
        ...(o.bold ? { fontWeight: 'bold' as const } : {}),
        fill: o.color,
        letterSpacing: o.bold ? 1 : 0,
      },
      resolution: this.opts.textResolution,
    });
    t.anchor.set(o.anchorX ?? 0, 0);
    t.position.set(x, y);
    this.body.addChild(t);
  }

  // centerX/centerY: the button is centered on this point (content centered on
  // its own origin in makeImageButton).
  private addMainButton(label: string, centerX: number, centerY: number, onTap: () => void): void {
    const btn = makeImageButton({
      texture: this.opts.selectBtn,
      label,
      height: 34,
      fontFamily: this.opts.fontFamily,
      fontSize: 14,
      textColor: 0x000000,
      textResolution: this.opts.textResolution,
      letterSpacing: 2,
      onTap,
    });
    btn.position.set(centerX, centerY);
    this.body.addChild(btn);
  }

  private addSmallButton(label: string, cx: number, y: number, onTap: () => void): void {
    const s = 22;
    const c = new Container();
    const bg = new Graphics()
      .roundRect(0, 0, s, s, 5)
      .fill({ color: 0x000000, alpha: 0.3 })
      .stroke({ color: PANEL_BORDER, width: 1 });
    c.addChild(bg);
    const t = new Text({
      text: label,
      style: { fontFamily: this.opts.fontFamily, fontSize: 14, fontWeight: 'bold', fill: LABEL_COLOR },
      resolution: this.opts.textResolution,
    });
    t.anchor.set(0.5);
    t.position.set(s / 2, s / 2);
    c.addChild(t);
    c.position.set(cx, y);
    c.eventMode = 'static';
    c.cursor = 'pointer';
    c.on('pointertap', onTap);
    this.body.addChild(c);
  }
}

function makeRow(n: number): ScriptCell[] {
  return Array.from({ length: n }, () => ({ ...DEFAULT_CELL }));
}

// Reads the saved script, coercing untrusted JSON into valid cells. Both rows
// are clamped to the same length (rounds = row count). Returns null on any
// missing / corrupt data so the caller falls back to a fresh grid.
function loadPersisted(): { p1: ScriptCell[]; p2: ScriptCell[] } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as { p1?: unknown; p2?: unknown };
    if (!Array.isArray(data.p1) || !Array.isArray(data.p2)) return null;
    const n = Math.min(data.p1.length, data.p2.length, MAX_ROUNDS);
    if (n < MIN_ROUNDS) return null;
    return {
      p1: data.p1.slice(0, n).map(coerceCell),
      p2: data.p2.slice(0, n).map(coerceCell),
    };
  } catch {
    return null;
  }
}
